import { CSSProperties, useEffect, useState } from 'react';
import { useReduxState } from 'hooks';

interface Props {
  backgroundColor?: string;
  borderColor?: string;
  borderProgressColor?: string;
  borderWidth?: number;
  label?: string;
  labelStyle?: CSSProperties;
  progress: number;
  style?: CSSProperties;
  width?: number;
}

const ProgressCircle = (props: Props) => {
  const { theme } = useReduxState();

  const {
    backgroundColor = 'white',
    borderColor = theme.lightGreyDark,
    borderProgressColor = 'rgba(0, 0, 0, 0.4)',
    borderWidth = 8,
    label,
    labelStyle,
    progress,
    style,
    width = 54
  } = props;

  const [isMounted, setIsMounted] = useState(false);

  const rotateRight = Math.min(180, Math.round(Math.min(progress, 1) * 360));
  const rotateLeft = Math.max(0, Math.round(Math.min(progress, 1) * 360) - 180);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  return (
    <div className="progress-circle" style={style}>
      <div className={`progress ${isMounted ? 'progress-mounted' : ''}`}>
        <div className="progress-content">
          <div className="progress-area progress-area-left">
            <div className="progress-inner progress-inner-left" />
          </div>
          <div className="progress-area progress-area-right">
            <div className="progress-inner progress-inner-right" />
          </div>
        </div>
      </div>

      {label ? (
        <div className="label" style={labelStyle}>
          <div className="label-inner">{label}</div>
        </div>
      ) : null}

      <style jsx>{`
        .progress-circle {
          align-items: center;
          display: flex;
          position: relative;
        }
        .label {
          align-items: center;
          color: ${theme.textColorLight};
          display: flex;
          font-size: 2rem;
          font-weight: 700;
          justify-content: center;
          line-height: 1;
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
        }
        .label-before {
          margin-right: 7px;
        }
        .label-after {
          margin-left: 7px;
        }
        .progress {
          background-color: ${backgroundColor};
          border-radius: 100%;
          position: relative;
          width: ${width}px;
          height: ${width}px;
        }
        .progress-content {
          border: ${borderWidth}px solid ${borderColor};
          border-radius: 100%;
          width: 100%;
          height: 100%;
        }
        .progress-area {
          overflow: hidden;
          position: absolute;
          top: 0;
          width: 50%;
        }
        .progress-area-left {
          height: ${progress > 0.5 ? `${width}px` : '0px'};
          left: 0;
        }
        .progress-area-right {
          height: ${width}px;
          right: 0;
        }
        .progress-inner {
          width: ${width}px;
          height: ${width}px;
          border: ${borderWidth}px solid transparent;
          border-radius: 50%;
          position: absolute;
          top: 0;
          transform: rotate(-135deg);
        }
        .progress-inner-left {
          border-bottom: ${borderWidth}px solid ${borderProgressColor};
          border-left: ${borderWidth}px solid ${borderProgressColor};
          left: 0px;
          transform: rotate(-135deg);
          transition: transform 1s linear 1s;
        }
        .progress-inner-right {
          border-top: ${borderWidth}px solid ${borderProgressColor};
          border-right: ${borderWidth}px solid ${borderProgressColor};
          right: 0px;
          transform: rotate(-135deg);
          transition: transform 1s linear;
        }
        .progress-mounted .progress-inner-left {
          transform: rotate(${-135 + rotateLeft}deg);
        }
        .progress-mounted .progress-inner-right {
          transform: rotate(${-135 + rotateRight}deg);
        }
      `}</style>
    </div>
  );
};

export default ProgressCircle;
